import { Upload, Cpu, MessageSquare, Quote, TrendingUp, Check } from "lucide-react";
import Seo from "../components/Seo";
import PublicHeader from "../components/PublicHeader";
import MarketingCta from "../components/MarketingCta";
import Footer from "../components/Footer";

const STEPS = [
  {
    icon: Upload,
    title: "Upload Your Agreements",
    desc: "Upload your collective agreement and supporting documents (PDF and standard formats). Your documents stay private — never used for model training, never externally indexed.",
  },
  {
    icon: Cpu,
    title: "CBAIQ Indexes and Understands the Content",
    desc: "CBAIQ processes the agreement at the clause level, building an index of articles, definitions, and provisions specific to your document.",
  },
  {
    icon: MessageSquare,
    title: "Ask Questions in Natural Language",
    desc: "Ask anything you'd ask a labour relations expert: \"When does double-time apply under Article 12?\" or \"Does this reassignment violate seniority provisions?\"",
  },
  {
    icon: Quote,
    title: "Get Answers with Citations",
    desc: "Every answer is structured and anchored in your agreement's actual text, citing the specific articles and language it relied on — so you can verify it against the source.",
  },
  {
    icon: TrendingUp,
    title: "Save Time and Reduce Risk",
    desc: "Teams answer agreement questions in seconds instead of hours, apply provisions consistently, and use risk-scored case review to understand grievance exposure before acting.",
  },
];

const BENEFITS = [
  "Citation-backed answers — every output references specific articles",
  "Consistent interpretation across people and departments",
  "Risk-scored case review before action is taken",
  "Secure storage, answer history, and exportable documentation",
  "Works for unions and employers alike",
];

const USE_CASES = [
  { category: "Overtime & Scheduling", example: "When does double-time apply, and how do shift premiums interact with overtime?" },
  { category: "Grievance Risk Review", example: "Does this proposed reassignment violate seniority or posting provisions?" },
  { category: "Discipline & Investigations", example: "What procedural protections and representation rights apply before a suspension?" },
  { category: "Leave & Accommodation", example: "How do sick leave, bereavement, and accommodation provisions apply to this scenario?" },
  { category: "Wages & Premiums", example: "How does the cost-of-living clause adjust pay this year?" },
  { category: "Negotiation Preparation", example: "Compare current language on layoff and recall against proposed changes." },
];

const HOWTO_JSONLD = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": "How CBAIQ Interprets Your Collective Agreement",
  "description": "How to get citation-backed answers from a collective agreement using CBAIQ: upload the agreement, let CBAIQ index it, ask questions in natural language, and receive answers cited to specific articles.",
  "step": STEPS.map(({ title, desc }, i) => ({
    "@type": "HowToStep",
    "position": i + 1,
    "name": title,
    "text": desc,
  })),
};

export default function HowItWorks() {
  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="how-it-works-page">
      <Seo
        title="How It Works | CBAIQ — From Agreement Upload to Cited Answers"
        description="See how CBAIQ works in 5 steps: upload your collective agreement, CBAIQ indexes it at the clause level, ask questions in plain English, and get structured answers with citations to specific articles."
        path="/how-it-works"
        jsonLd={HOWTO_JSONLD}
      />
      <PublicHeader />

      <div className="flex-1">
        {/* Hero */}
        <section className="pt-20 sm:pt-24 pb-12 px-4" data-testid="hiw-hero">
          <div className="max-w-3xl mx-auto">
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium mb-4">How It Works</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold leading-tight tracking-tight text-foreground mb-5">
              From Agreement Upload to Cited Answers in Minutes
            </h1>
            <p className="text-base text-muted-foreground leading-relaxed">
              CBAIQ turns your collective agreement into a system you can question in plain English —
              with every answer traceable to the specific articles it came from.
            </p>
          </div>
        </section>

        {/* Steps */}
        <section className="py-12 border-t border-border/40 px-4" data-testid="hiw-steps">
          <div className="max-w-3xl mx-auto">
            <ol className="space-y-0">
              {STEPS.map(({ icon: Icon, title, desc }, i) => (
                <li key={title} className="relative flex gap-6 pb-12 last:pb-0" data-testid={`hiw-step-${i + 1}`}>
                  {/* connector line */}
                  {i < STEPS.length - 1 && (
                    <span className="absolute left-6 top-12 bottom-0 w-px bg-border" aria-hidden="true" />
                  )}
                  <div className="flex-shrink-0 w-12 h-12 rounded-full border border-border bg-background flex items-center justify-center z-10">
                    <Icon className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div className="pt-1.5">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium mb-1.5">Step {i + 1}</p>
                    <h2 className="text-lg font-semibold text-foreground mb-2">{title}</h2>
                    <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-16 border-y border-border/40 bg-muted/20 px-4" data-testid="hiw-benefits">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-foreground mb-8">Why Teams Use CBAIQ</h2>
            <ul className="space-y-4">
              {BENEFITS.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-foreground/60 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Use cases */}
        <section className="py-16 px-4" data-testid="hiw-use-cases">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-foreground mb-3">Common Use Cases</h2>
            <p className="text-sm text-muted-foreground max-w-lg mb-10">
              Each question is resolved through clause-level analysis of your uploaded agreement.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {USE_CASES.map(({ category, example }) => (
                <div key={category} className="border border-border rounded-lg p-6" data-testid={`hiw-usecase-${category.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium mb-3">{category}</p>
                  <p className="text-sm text-foreground leading-relaxed">"{example}"</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <MarketingCta page="hiw" heading="See It Work on Your Own Agreement." />
      </div>
      <Footer />
    </div>
  );
}
